import { useState, useRef, useEffect } from "react";
import { FiChevronDown, FiSearch } from "react-icons/fi";
import useOutsideClick from "../hooks/useOutsideClick";

interface Option {
    value: string;
    label: string;
}

interface CustomSelectProps {
    label?: string;
    name: string;
    value: string;
    onChange: (name: string, value: string) => void;
    options: Option[];
    placeholder?: string;
    required?: boolean;
    disableSearch?: boolean;
    className?: string;
    helperText?: string;
    isDark?: boolean;
}

export default function CustomSelect({
    label,
    name,
    value,
    onChange,
    options,
    placeholder = "Select...",
    disableSearch = false,
    className = "",
    helperText,
    required = false,
    isDark = false,
}: CustomSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const searchInputRef = useRef<HTMLInputElement>(null);

    const containerRef = useOutsideClick(() => {
        setIsOpen(false);
        setSearchTerm("");
    });

    // Focus search input when opened
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);

    const selectedOption = options.find((opt) => opt.value === value);

    const filteredOptions = options.filter((opt) =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="flex flex-col w-full relative" ref={containerRef}>
            {label && (
                <label className={`mb-1.5 text-xs font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {label}{required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}
            {helperText && <p className="fmtv-helper-text">{helperText}</p>}

            {/* Trigger Button */}
            <div
                className={`
                    flex items-center justify-between py-2.5 px-3.5 rounded-xl cursor-pointer transition-all duration-200 text-xs font-medium
                    ${isDark
                        ? isOpen
                            ? "bg-white/[0.08] border-[#B08D55] ring-1 ring-[#B08D55] text-white"
                            : "bg-white/[0.04] border border-white/[0.08] text-white hover:border-[#B08D55]/50 hover:bg-white/[0.06]"
                        : isOpen
                            ? "bg-white border-[#B08D55] ring-1 ring-[#B08D55] text-gray-900"
                            : "bg-white border border-gray-300 text-gray-900 hover:border-[#B08D55]"
                    }
                    ${className}
                `}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className={selectedOption ? (isDark ? "text-white font-medium" : "text-gray-900 font-medium") : "text-gray-500"}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <FiChevronDown
                    className={`transition-transform duration-200 ${isOpen ? "rotate-180 text-[#B08D55]" : isDark ? "text-gray-400" : "text-gray-500"}`}
                />
            </div>

            {/* Dropdown Menu */}
            {isOpen && (
                <div
                    className={`absolute z-50 top-[calc(100%+6px)] left-0 w-full rounded-xl shadow-2xl overflow-hidden ${
                        isDark
                            ? "bg-[#131b2e] border border-white/15"
                            : "bg-white border border-gray-200"
                    }`}
                >
                    {/* Search Input */}
                    {!disableSearch && (
                        <div className={`p-2 border-b ${isDark ? 'border-white/10 bg-black/20' : 'border-gray-100 bg-gray-50'}`}>
                            <div className="relative">
                                <FiSearch className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    className={`w-full pl-9 pr-3 py-1.5 text-xs rounded-lg focus:outline-none focus:border-[#B08D55] ${
                                        isDark
                                            ? 'bg-white/5 border border-white/10 text-white placeholder-gray-500'
                                            : 'bg-white border border-gray-200 text-gray-900 placeholder-gray-500'
                                    }`}
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        </div>
                    )}

                    {/* Options List */}
                    <div className="relative">
                        <div className="max-h-52 overflow-y-auto custom-scrollbar p-1">
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map((opt) => (
                                    <div
                                        key={opt.value}
                                        className={`
                                            px-3.5 py-2.5 cursor-pointer text-xs rounded-lg transition-colors font-medium
                                            ${isDark
                                                ? value === opt.value
                                                    ? "bg-[#B08D55]/25 text-[#B08D55] font-bold"
                                                    : "text-gray-200 hover:bg-white/[0.08] hover:text-white"
                                                : value === opt.value
                                                    ? "bg-[#B08D55]/10 text-[#B08D55] font-bold"
                                                    : "text-gray-700 hover:bg-gray-50"
                                            }
                                        `}
                                        onClick={() => {
                                            onChange(name, opt.value);
                                            setIsOpen(false);
                                            setSearchTerm("");
                                        }}
                                    >
                                        {opt.label}
                                    </div>
                                ))
                            ) : (
                                <div className="p-4 text-center text-gray-400 text-xs italic">
                                    No results found
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
